import { Box, Link, Typography } from "@mui/material";
import React from "react";
import { Link as RouterLink } from "react-router-dom";

import { monospaceStyle } from "../../theme";
import { CountedEntity } from "../../types/capabilityPartDetail";
import { EntityRef } from "../../types/productPlane";
import EntityLink from "../Detail/EntityLink";

// Where the count of an entry leads: the list that gives the same number
export interface CountLink {
  // The address of the list
  address: string;
  // What the link is, for a screen reader
  label: string;
}

// The last entry of a list: the products that name none of its entries
export interface NoneEntry {
  // What the entry reads, e.g. "no condition stated"
  label: string;
  count: number;
  // The products the count is out of
  out_of: number;
}

export interface PartCountedListProps {
  // The entries, the largest count first
  items: CountedEntity[];
  // Entries in the whole list, when the response holds only its start
  total?: number;
  // Where the count of an entry leads; null for an entry whose count has no
  // list that gives the same number. Without it no count is a link.
  countLink?: (entity: EntityRef) => CountLink | null;
  // Whether each count has a thin bar filled to its share
  bars?: boolean;
  // The greyed last entry; left out when absent or when it counts nothing
  none?: NoneEntry | null;
}

// Width and height (px) of the bar beside a count
const BAR_WIDTH = 64;
const BAR_HEIGHT = 4;

// The colour a bar is filled with: the products that name an entry, or, in
// grey, those that name none
type BarTone = "primary" | "grey";

const BAR_FILL: Record<BarTone, string> = {
  primary: "primary.main",
  grey: "text.disabled",
};

/**
 * A count out of a whole: "12 of 41" in digits of equal width, beside a
 * thin bar filled to that share where there is a bar.
 */
const Share: React.FC<{
  count: number;
  outOf: number;
  tone: BarTone | null;
}> = ({ count, outOf, tone }) => {
  const text = `${count} of ${outOf}`;
  const share = outOf > 0 ? Math.min(1, Math.max(0, count / outOf)) : 0;
  return (
    <Box
      component="span"
      aria-label={`${text} products`}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        whiteSpace: "nowrap",
      }}
    >
      {tone && (
        <Box
          component="span"
          aria-hidden
          sx={{
            display: "inline-block",
            width: BAR_WIDTH,
            height: BAR_HEIGHT,
            borderRadius: `${BAR_HEIGHT / 2}px`,
            backgroundColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box
            component="span"
            sx={{
              display: "block",
              width: `${share * 100}%`,
              height: "100%",
              backgroundColor: BAR_FILL[tone],
            }}
          />
        </Box>
      )}
      <Box
        component="span"
        sx={{ ...monospaceStyle, fontSize: "0.75rem", color: "text.secondary" }}
      >
        {text}
      </Box>
    </Box>
  );
};

// One line of the list: the name at the left, the count at the right
const lineSx = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  columnGap: 2,
} as const;

/**
 * Entities with the products that count towards each: the name, which opens
 * the entity, and the count out of its whole, with a thin bar where the
 * list has bars. A count leads to its list where there is one. A greyed
 * last line counts the products that name no entry.
 *
 * Renders nothing without an entry.
 */
const PartCountedList: React.FC<PartCountedListProps> = ({
  items,
  total,
  countLink,
  bars = true,
  none,
}) => {
  if (items.length === 0) {
    return null;
  }

  return (
    <Box>
      <Box
        component="ul"
        sx={{
          listStyle: "none",
          m: 0,
          p: 0,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        {items.map((item) => {
          const link = countLink ? countLink(item.entity) : null;
          const share = (
            <Share
              count={item.count}
              outOf={item.out_of}
              tone={bars ? "primary" : null}
            />
          );
          return (
            <Typography
              key={item.entity.id}
              component="li"
              variant="body2"
              sx={lineSx}
            >
              <EntityLink
                id={item.entity.id}
                type={item.entity.type}
                name={item.entity.name}
              />
              {link ? (
                <Link
                  component={RouterLink}
                  to={link.address}
                  aria-label={link.label}
                  title={link.label}
                  underline="hover"
                  sx={{ flexShrink: 0 }}
                >
                  {share}
                </Link>
              ) : (
                <Box component="span" sx={{ flexShrink: 0 }}>
                  {share}
                </Box>
              )}
            </Typography>
          );
        })}
        {none && none.count > 0 && (
          <Typography
            component="li"
            variant="body2"
            sx={{ ...lineSx, color: "text.secondary" }}
          >
            <span>{none.label}</span>
            <Box component="span" sx={{ flexShrink: 0 }}>
              <Share
                count={none.count}
                outOf={none.out_of}
                tone={bars ? "grey" : null}
              />
            </Box>
          </Typography>
        )}
      </Box>
      {total !== undefined && total > items.length && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ...monospaceStyle, display: "block", mt: 0.5 }}
        >
          the first {items.length} of {total}
        </Typography>
      )}
    </Box>
  );
};

export default PartCountedList;
